import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './Toast';

const AdminRAG = () => {
  const { addToast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [datagouvStatus, setDatagouvStatus] = useState(null);

  useEffect(() => {
    loadDocuments();
    checkDatagouvStatus();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/knowledge/documents');
      if (response.success) {
        setDocuments(response.documents || []);
      }
    } catch (err) {
      addToast('Erreur chargement documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier l'extension
    const allowedExtensions = ['.pdf', '.txt', '.md', '.docx'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      addToast('Format non supporté. Utilisez PDF, TXT, MD ou DOCX', 'error');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/admin/knowledge/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        addToast(`Document "${file.name}" uploadé avec succès`, 'success');
        loadDocuments();
      } else {
        throw new Error(result.detail || 'Erreur upload');
      }
    } catch (err) {
      addToast(`Erreur upload: ${err.message}`, 'error');
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDelete = async (filename) => {
    if (!confirm(`Supprimer "${filename}" ?`)) return;

    try {
      const response = await api.delete(`/admin/knowledge/documents/${filename}`);
      if (response.success) {
        addToast(`Document "${filename}" supprimé`, 'success');
        loadDocuments();
      }
    } catch (err) {
      addToast('Erreur suppression document', 'error');
    }
  };

  const handleUpdateIndex = async () => {
    setUpdating(true);
    try {
      const response = await api.post('/admin/knowledge/update');
      if (response.success) {
        addToast('Base de connaissance RAG mise à jour !', 'success');
      } else {
        addToast(response.message || 'Erreur mise à jour', 'warning');
      }
    } catch (err) {
      addToast('Erreur mise à jour index', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const checkDatagouvStatus = async () => {
    try {
      const response = await api.get('/admin/knowledge/datagouv/status');
      setDatagouvStatus(response);
    } catch (err) {
      setDatagouvStatus({ success: false, status: 'error' });
    }
  };

  const handleSyncDatagouv = async () => {
    if (!confirm('Lancer la synchronisation avec data.gouv.fr ?\n\nCela peut prendre plusieurs minutes.')) {
      return;
    }

    setSyncing(true);
    try {
      const response = await api.post('/admin/knowledge/datagouv/sync', {
        queries: [
          'fiscalité locale professionnels REI',
          'Recensement Éléments Imposition',
          'bases de données fiscales locales'
        ]
      });

      if (response.success) {
        addToast('Synchronisation lancée en arrière-plan', 'success');
        // Recharger les documents après 30 secondes
        setTimeout(() => {
          loadDocuments();
        }, 30000);
      }
    } catch (err) {
      addToast('Erreur synchronisation data.gouv.fr', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px'}}>
        <div style={{textAlign: 'center'}}>
          <div style={{width: '40px', height: '40px', border: '3px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite'}}></div>
          <p style={{color: '#73726C', fontSize: '14px'}}>Chargement...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{padding: '24px', maxWidth: '1200px', margin: '0 auto'}}>
      {/* Header */}
      <div style={{marginBottom: '32px'}}>
        <h2 style={{fontSize: '24px', fontWeight: '600', color: '#2D2D2D', marginBottom: '8px'}}>
          📚 Base de Connaissance RAG
        </h2>
        <p style={{fontSize: '14px', color: '#73726C'}}>
          Gérez les documents fiscaux utilisés par l'IA pour répondre aux questions
        </p>
      </div>

      {/* Actions */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '32px'}}>
        {/* Upload */}
        <div style={{background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #E8E7E3'}}>
          <h3 style={{fontSize: '16px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>
            📤 Ajouter un document
          </h3>
          <input
            type="file"
            accept=".pdf,.txt,.md,.docx"
            onChange={handleFileUpload}
            disabled={uploading}
            style={{display: 'none'}}
            id="ragFileInput"
          />
          <label
            htmlFor="ragFileInput"
            style={{
              display: 'block',
              width: '100%',
              padding: '12px',
              background: uploading ? '#73726C' : '#1D9E75',
              color: 'white',
              textAlign: 'center',
              borderRadius: '8px',
              cursor: uploading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {uploading ? 'Upload en cours...' : 'Choisir un fichier (PDF, TXT, MD, DOCX)'}
          </label>
        </div>

        {/* Update Index */}
        <div style={{background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #E8E7E3'}}>
          <h3 style={{fontSize: '16px', fontWeight: '600', color: '#2D2D2D', marginBottom: '12px'}}>
            🔄 Mettre à jour l'index
          </h3>
          <p style={{fontSize: '13px', color: '#73726C', marginBottom: '12px'}}>
            À exécuter après ajout/suppression de documents
          </p>
          <button
            onClick={handleUpdateIndex}
            disabled={updating}
            style={{
              width: '100%',
              padding: '12px',
              background: updating ? '#73726C' : '#0F6E56',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: updating ? 'not-allowed' : 'pointer'
            }}
          >
            {updating ? 'Mise à jour...' : 'Regénérer l\'index vectoriel'}
          </button>
        </div>
      </div>

      {/* Data.gouv.fr Sync */}
      <div style={{background: 'white', padding: '20px', borderRadius: '12px', border: '2px solid #1D9E75', marginBottom: '32px'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px'}}>
          <div style={{width: '40px', height: '40px', borderRadius: '8px', background: '#E1F5EE', color: '#0F6E56', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px'}}>
            🇫🇷
          </div>
          <div style={{flex: 1}}>
            <h3 style={{fontSize: '16px', fontWeight: '600', color: '#2D2D2D', marginBottom: '4px'}}>
              Synchronisation data.gouv.fr
            </h3>
            <p style={{fontSize: '13px', color: '#73726C'}}>
              Protocole MCP — Fiscalité locale, REI, bases de données fiscales
            </p>
          </div>
          {datagouvStatus && (
            <div style={{
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '500',
              background: datagouvStatus.success ? '#E1F5EE' : '#FEE2E2',
              color: datagouvStatus.success ? '#0F6E56' : '#991B1B'
            }}>
              {datagouvStatus.status === 'available' ? '✓ Connecté' : '✗ Indisponible'}
            </div>
          )}
        </div>

        <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
          <button
            onClick={handleSyncDatagouv}
            disabled={syncing || !datagouvStatus?.success}
            style={{
              flex: 1,
              padding: '12px',
              background: syncing || !datagouvStatus?.success ? '#73726C' : '#1D9E75',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: syncing || !datagouvStatus?.success ? 'not-allowed' : 'pointer'
            }}
          >
            {syncing ? 'Synchronisation...' : 'Lancer la synchronisation'}
          </button>
          <button
            onClick={checkDatagouvStatus}
            style={{
              padding: '12px 16px',
              background: 'transparent',
              color: '#1D9E75',
              border: '1px solid #1D9E75',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Vérifier
          </button>
        </div>

        {datagouvStatus && datagouvStatus.success && datagouvStatus.mcp_tools && (
          <div style={{marginTop: '12px', padding: '12px', background: '#FAF9F5', borderRadius: '8px'}}>
            <div style={{fontSize: '11px', color: '#73726C', marginBottom: '4px'}}>Outils MCP disponibles:</div>
            <div style={{fontSize: '12px', color: '#2D2D2D'}}>
              {datagouvStatus.mcp_tools.join(', ')}
            </div>
          </div>
        )}
      </div>

      {/* Documents List */}
      <div>
        <h3 style={{fontSize: '18px', fontWeight: '600', color: '#2D2D2D', marginBottom: '16px'}}>
          Documents ({documents.length})
        </h3>

        {documents.length === 0 ? (
          <div style={{background: '#FAF9F5', border: '1px dashed #E8E7E3', borderRadius: '12px', padding: '48px', textAlign: 'center'}}>
            <div style={{fontSize: '48px', marginBottom: '12px'}}>📭</div>
            <p style={{fontSize: '14px', color: '#73726C', marginBottom: '16px'}}>
              Aucun document dans la base de connaissance
            </p>
            <p style={{fontSize: '13px', color: '#9CA3AF'}}>
              Ajoutez des guides fiscaux, textes de loi, ou instructions officielles
            </p>
          </div>
        ) : (
          <div style={{display: 'grid', gap: '12px'}}>
            {documents.map((doc, idx) => (
              <div
                key={idx}
                style={{
                  background: 'white',
                  border: '1px solid #E8E7E3',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <div style={{width: '40px', height: '40px', borderRadius: '8px', background: '#E1F5EE', color: '#0F6E56', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px'}}>
                  📄
                </div>
                <div style={{flex: 1}}>
                  <div style={{fontSize: '14px', fontWeight: '500', color: '#2D2D2D', marginBottom: '4px'}}>
                    {doc.filename}
                  </div>
                  <div style={{fontSize: '12px', color: '#73726C'}}>
                    {formatFileSize(doc.size)} · {new Date(doc.created).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(doc.filename)}
                  style={{
                    padding: '8px 16px',
                    background: '#FEE2E2',
                    color: '#991B1B',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Supprimer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: '8px', padding: '12px', marginTop: '24px'}}>
        <div style={{display: 'flex', gap: '8px', fontSize: '12px', color: '#1E40AF'}}>
          <span>ℹ️</span>
          <div>
            <strong style={{color: '#1E40AF'}}>Documents recommandés</strong> : BOFIP, instructions fiscales, code général des impôts, seuils et barèmes 2025.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminRAG;
